import axios from "axios";

export type FeedbackFormValues = {
  fullName: string;
  position: string;
  phone: string;
  email?: string;
  comment?: string;
  tender?: boolean;
};

export type SendFeedbackPayload = FeedbackFormValues & {
  captchaToken: string;
};

type AnyObj = Record<string, any>;

const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
const B2B_BASE = (process.env.REACT_APP_B2B_URL || "").replace(/\/+$/, "");

const isAbsoluteUrl = (value: string) =>
  /^https?:\/\//i.test(value) ||
  value.startsWith("//") ||
  value.startsWith("data:") ||
  value.startsWith("blob:");

const joinUrl = (base: string, path: string) => {
  const b = (base || "").replace(/\/+$/, "");
  const p = path.startsWith("/") ? path : `/${path}`;
  return b ? `${b}${p}` : path;
};

const resolveAssetUrl = (value: string) => {
  if (!value) return value;
  if (isAbsoluteUrl(value)) return value;

  if (value.startsWith("/assets/") || value.startsWith("assets/")) {
    if (!B2B_BASE) return value;
    return joinUrl(B2B_BASE, value.startsWith("/") ? value : `/${value}`);
  }

  return value;
};

const resolveAssetsInHtml = (html: string) => {
  if (!html || !B2B_BASE) return html;
  return html.replace(
    /(src|href)=["'](\/assets\/[^"']+)["']/g,
    (_m, attr, path) => `${attr}="${joinUrl(B2B_BASE, path)}"`,
  );
};

const deepResolveAssets = (input: any): any => {
  if (Array.isArray(input)) return input.map(deepResolveAssets);

  if (input && typeof input === "object") {
    const out: AnyObj = {};
    for (const [k, v] of Object.entries(input)) {
      if (typeof v === "string" && (k === "textHtml" || k.endsWith("Html"))) {
        out[k] = resolveAssetsInHtml(v);
      } else {
        out[k] = deepResolveAssets(v);
      }
    }
    return out;
  }

  if (typeof input === "string") return resolveAssetUrl(input);
  return input;
};

export const normalizeLangCode = (lang: string) => {
  const raw = (lang || "ua").toLowerCase().trim();
  const base = raw.split("-")[0].split("_")[0];

  if (base === "uk") return "ua";
  return base || "ua";
};

const isObject = (value: unknown): value is AnyObj =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const deepMerge = (base: any, patch: any): any => {
  if (Array.isArray(base)) {
    return Array.isArray(patch) ? patch : base;
  }

  if (isObject(base)) {
    if (!isObject(patch)) return base;

    const result: AnyObj = { ...base };
    for (const key of Object.keys(patch)) {
      result[key] = key in base ? deepMerge(base[key], patch[key]) : patch[key];
    }

    return result;
  }

  return patch === undefined ? base : patch;
};

const defaultLandingFromAssets = {
  common: {
    send: "",
    scrollTopLabel: "",
    sliderAria: {
      prev: "",
      next: "",
      pagination: "",
      goToSlide: "",
    },
  },
  header: {
    retailUrl: "#",
    phone: "",
    phoneHref: "",
    email: "",
    logo: "/assets/logo.svg",
    logoAlt: "logo",
    languageLabels: {
      ua: "UA",
      en: "EN",
    },
    feedbackLabel: "",
    loginLabel: "",
    retailText: "",
    menuAriaLabel: "menu",
    retailAriaLabel: "retail",
    homeAriaLabel: "home",
    loginAriaLabel: "login",
    languageAriaLabel: "language",
    closeAriaLabel: "close",
  },
  hero: {
    bannerDesktop: "/assets/banners/hero-desktop.png",
    bannerMobile: "/assets/banners/hero-mobile.png",
    titlePrefix: "",
    titleAccent: "",
    subtitle: "",
    buttons: [],
  },
  reviews: {
    title: "",
    items: [],
  },
  offer: {
    title: "",
    tabsAriaLabel: "tabs",
    tabs: {
      products: "",
      services: "",
    },
    products: [],
    services: [],
  },
  consultation: {
    bg: "/assets/pattern-red.png",
    textHtml: "",
    submitLabel: "",
    thanksText: "",
  },
  advantages: {
    title: "",
    items: [],
  },
  about: {
    anchor: "",
    textHtml: "",
    tiles: [],
  },
  clients: {
    title: "",
    logoAltFallback: "logo",
    items: [],
  },
  promo: {
    title: "",
    description: "",
    buttonText: "",
    ribbonText: "",
  },
  awards: {
    title: "",
    imageAltFallback: "award",
    items: [],
  },
  feedbackModal: {
    title: "",
    thanksText: "",
    submitLabel: "",
  },
  lead: {
    bg: "/assets/lead-bg.png",
    textHtml: "",
    submitLabel: "",
    thanksText: "",
  },
  scrollTop: {
    label: "",
  },
  footer: {
    logo: "/assets/logo.svg",
    logoAlt: "logo",
    hotlineTitle: "",
    hotlinePhone: "",
    hotlinePhoneHref: "",
    hotlineEmail: "",
    tenderTitle: "",
    tenderPhone: "",
    tenderPhoneHref: "",
    tenderEmail: "",
    writeTitle: "",
    feedbackLabel: "",
    productsTitle: "",
    catalogLabel: "",
    catalogUrl: "#",
  },
  feedbackForm: {
    successIconAlt: "",
  },
  authModal: {},
};

const defaultResolvedHome = deepResolveAssets(defaultLandingFromAssets);

const homeCache = new Map<string, any>();
const homePromiseCache = new Map<string, Promise<any>>();

const codesCacheKey = "landing-codes";
const codesCache = new Map<string, string[]>();
const codesPromiseCache = new Map<string, Promise<string[]>>();

const normalizeCodes = (codes: string[]) => {
  const unique = Array.from(
    new Set(
      (codes || [])
        .map((code) => normalizeLangCode(String(code || "")))
        .filter(Boolean),
    ),
  );

  if (!unique.length) return ["ua"];

  unique.sort((a, b) => a.localeCompare(b));
  if (unique.includes("ua")) {
    return ["ua", ...unique.filter((code) => code !== "ua")];
  }

  return unique;
};

const fetchLandingByCode = async (code: string) => {
  const res = await fetch(`${API_BASE}/landing/lang/${code}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  const data = await res.json();
  const landingJson = data?.json ?? data;
  if (!landingJson || typeof landingJson !== "object") {
    throw new Error("Invalid landing payload");
  }

  const merged = deepMerge(defaultLandingFromAssets, landingJson);
  return deepResolveAssets(merged);
};

export const api = {
  async getLandingCodes() {
    if (codesCache.has(codesCacheKey)) return codesCache.get(codesCacheKey)!;
    if (codesPromiseCache.has(codesCacheKey)) {
      return codesPromiseCache.get(codesCacheKey)!;
    }

    const p = (async () => {
      if (!API_BASE) {
        const localCodes = normalizeCodes(["ua"]);
        codesCache.set(codesCacheKey, localCodes);
        return localCodes;
      }

      try {
        const res = await fetch(`${API_BASE}/landing/codes`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const payload = await res.json();
        const normalized = normalizeCodes(Array.isArray(payload) ? payload : []);
        codesCache.set(codesCacheKey, normalized);
        return normalized;
      } catch {
        const fallback = normalizeCodes(["ua"]);
        codesCache.set(codesCacheKey, fallback);
        return fallback;
      } finally {
        codesPromiseCache.delete(codesCacheKey);
      }
    })();

    codesPromiseCache.set(codesCacheKey, p);
    return p;
  },

  async getHome(lang: string) {
    const code = normalizeLangCode(lang);

    if (homeCache.has(code)) return homeCache.get(code);
    if (homePromiseCache.has(code)) return homePromiseCache.get(code);

    const p = (async () => {
      if (!API_BASE) {
        homeCache.set(code, defaultResolvedHome);
        return defaultResolvedHome;
      }

      try {
        const resolved = await fetchLandingByCode(code);
        homeCache.set(code, resolved);
        return resolved;
      } catch {
        if (code !== "ua") {
          try {
            const fallbackUa = await fetchLandingByCode("ua");
            homeCache.set(code, fallbackUa);
            return fallbackUa;
          } catch {
            // continue to local fallback
          }
        }

        homeCache.set(code, defaultResolvedHome);
        return defaultResolvedHome;
      } finally {
        homePromiseCache.delete(code);
      }
    })();

    homePromiseCache.set(code, p);
    return p;
  },

  async sendFeedback(payload: SendFeedbackPayload) {
    const { data } = await axios.post(`${API_BASE}/feedbacks`, payload);
    return data;
  },
};
